import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { getWeekdayLabel, getMonthDayLabel } from "../utils/formatDateLabels";
import { useI18n } from "../i18n/i18n";
import { getLocalizedExerciseName } from "../utils/exerciseName";
import { getAllExercisesCatalog } from "../services/customExerciseService";

const buildExerciseLookup = () => Object.fromEntries(getAllExercisesCatalog().map((item) => [item.id, item]));

const truncateToTwoWords = (text) => {
    if (typeof text !== "string") {
        return text;
    }

    const words = text.trim().split(/\s+/).filter(Boolean);
    if (words.length <= 2) {
        return text;
    }

    return `${words.slice(0, 2).join(" ")}...`;
};

function collectExercisesForDate({ date, exerciseLog, exerciseMap, t }) {
    return Object.entries(exerciseLog[date] || {}).map(([exerciseId]) => {
        const definition = exerciseMap[exerciseId];
        const fallbackName = t("home.unknownExercise", { id: exerciseId });

        return {
            id: exerciseId,
            date,
            name: definition ? getLocalizedExerciseName(definition, t) : fallbackName,
        };
    });
}

export default function ExerciseProgressTable({ dates, exerciseLog }) {
    const { t, locale, localizePath } = useI18n();
    const exerciseMap = useMemo(buildExerciseLookup, []);
    const rows = useMemo(
        () =>
            dates.map((date) => ({
                key: date,
                weekday: getWeekdayLabel(date, locale),
                monthDay: getMonthDayLabel(date, locale),
                exercises: collectExercisesForDate({ date, exerciseLog, exerciseMap, t }),
            })),
        [dates, locale, exerciseLog, exerciseMap, t]
    );

    return (
        <table className="compactExerciseTable progressByDateTable" aria-label={t("home.exerciseHistoryTable")}>
            <thead>
            <tr>
                <th>{t("home.date")}</th>
                <th>{t("home.exercise")}</th>
            </tr>
            </thead>
            <tbody>
            {rows.map(({ key, weekday, monthDay, exercises }) => (
                <tr key={key}>
                    <td className="progressDateCell">
                        <div className="dateHeaderWeekday">{weekday}</div>
                        <div className="dateHeaderDate">{monthDay}</div>
                    </td>
                    <td>
                        <div className="exerciseGroup">
                            {exercises.map(({ id, date, name }) => (
                                <Link
                                    key={`${id}-${date}`}
                                    to={localizePath(`/LogExercise/${id}/${date}`)}
                                    className="exerciseLink"
                                >
                                    <span>{truncateToTwoWords(name)}</span>
                                </Link>
                            ))}
                        </div>
                    </td>
                </tr>
            ))}
            </tbody>
        </table>
    );
}
